/**
 * Padding — the ONE place the `padding` shorthand and its four optional
 * per-side overrides are turned into effective values:
 *
 *   left   = pad_left   ?? pad_hor ?? padding ?? 0
 *   right  = pad_right  ?? pad_hor ?? padding ?? 0
 *   top    = pad_top    ?? pad_ver ?? padding ?? 0
 *   bottom = pad_bottom ?? pad_ver ?? padding ?? 0
 *
 * pad_hor/pad_ver are the AXIS-level overrides (shared.js's PADDING_AXES) a
 * part declares instead of the four sides when its widget can only distinguish
 * horizontal from vertical padding — the checkbox bullet, whose box is sized
 * per axis. A part declares one set or the other, never both, but resolution
 * handles all of them uniformly so no drawing code has to care which it is.
 *
 * Widgets never re-derive that fallback themselves — they call resolvePadding()
 * (or contentBox()/resolveRadialPadding()) with an already-resolved PartStyle,
 * i.e. the object node.style(part) returns, so themes, schema defaults, local
 * overrides and state overrides are all included by construction (the cascade
 * itself stays entirely in StyleResolver.js).
 *
 * The four sides are ordinary style properties: for the `main` part their wire
 * keys are literally pad_top/pad_right/pad_bottom/pad_left, so export/import
 * flow through the generic paths. `padding` remains `exportSpecial` (1 value →
 * 4 wire keys, see ObjectExport.js) and stays the convenience property.
 */

/** The four per-side override property names, in Property Editor order (top, right, bottom, left). */
export const PADDING_SIDE_PROPS = ['pad_top', 'pad_right', 'pad_bottom', 'pad_left']

/** The axis-level override property names (see PADDING_AXES in shared.js). */
export const PADDING_AXIS_PROPS = ['pad_hor', 'pad_ver']

/** Side property name -> the effective-value key resolvePadding returns it as. */
export const PADDING_SIDE_KEYS = {
    pad_top: 'top',
    pad_right: 'right',
    pad_bottom: 'bottom',
    pad_left: 'left',
}

function num(value) {
    if (value === undefined || value === null || value === '') return undefined
    const n = Number(value)
    return Number.isFinite(n) ? n : undefined
}

/**
 * Effective per-side padding for one resolved part style.
 * @param style a resolved PartStyle (node.style(part[, state])) — may be undefined
 * @returns {{ left: number, right: number, top: number, bottom: number }}
 */
export function resolvePadding(style, { overallScale = 1 } = {}) {
    const overall = (num(style?.padding) ?? 0) * overallScale
    const hor = num(style?.pad_hor) ?? overall
    const ver = num(style?.pad_ver) ?? overall
    return {
        top: num(style?.pad_top) ?? ver,
        right: num(style?.pad_right) ?? hor,
        bottom: num(style?.pad_bottom) ?? ver,
        left: num(style?.pad_left) ?? hor,
    }
}

/** Legacy knob shorthand compatibility: `padding` is the total extra knob
 * size, while direct pad_* properties are raw LVGL per-side values. */
export function resolveKnobPadding(style) {
    return resolvePadding(style, { overallScale: 0.5 })
}

/** True when the four effective sides are all the same value (the condition for the Overall control being editable). */
export function isUniformPadding(style, options) {
    const { top, right, bottom, left } = resolvePadding(style, options)
    return top === right && right === bottom && bottom === left
}

/**
 * Presentation state shared by compact padding editors. `value` is omitted
 * for mixed sides so a caller cannot accidentally display an average or one
 * arbitrary side as the overall value.
 */
export function paddingEditorState(style, options) {
    const effective = resolvePadding(style, options)
    const uniform = effective.top === effective.right
        && effective.right === effective.bottom
        && effective.bottom === effective.left
    return {
        effective,
        enabled: uniform,
        value: uniform ? effective.top : undefined,
    }
}

/**
 * The content box left after insetting a widget's outer box by its effective
 * padding — the replacement for the old `x + padding / width - padding * 2`
 * arithmetic scattered across widget drawing code.
 */
export function contentBox(style, width, height) {
    const { left, right, top, bottom } = resolvePadding(style)
    return {
        x: left,
        y: top,
        width: Math.max(0, (Number(width) || 0) - left - right),
        height: Math.max(0, (Number(height) || 0) - top - bottom),
    }
}

/**
 * Per-side padding expressed for the radial widgets (arc, spinner), whose
 * geometry is a centre point plus a radius rather than a box: an asymmetric
 * pad shifts the centre by half the difference and the radius shrinks by the
 * larger of the two axes' mean inset. Reduces to `centre unchanged, radius -
 * padding` for a uniform padding, i.e. identical to the pre-per-side maths.
 */
export function resolveRadialPadding(style) {
    const { left, right, top, bottom } = resolvePadding(style)
    return {
        offsetX: (left - right) / 2,
        offsetY: (top - bottom) / 2,
        inset: Math.max((left + right) / 2, (top + bottom) / 2),
    }
}

/**
 * Subscribe one redraw handler to the shorthand AND all four side overrides —
 * so a widget that already redrew on `padding` keeps redrawing on every edit
 * without listing five property names at each call site.
 */
export function onPaddingStyle(node, part, handler) {
    node.onStyle(part, 'padding', handler)
    for (const prop of [...PADDING_SIDE_PROPS, ...PADDING_AXIS_PROPS]) node.onStyle(part, prop, handler)
}

// ---------------------------------------------------------------------------
// Property Editor write helpers — kept here (not in the Vue component) so the
// normalization rule is testable on its own and lives next to the resolution
// rule it mirrors. `node` is any HaspObject: style()/setStateStyle()/
// resetStateStyle() only. Every write happens synchronously in one burst, so
// App.vue's debounced PropertyChangeCommand records it as ONE history entry.
// ---------------------------------------------------------------------------

/** Overall padding: either write Gauge's shorthand or four raw side values. */
export function applyOverallPadding(node, part, state, value, { writeSides = false } = {}) {
    if (writeSides) {
        for (const prop of PADDING_SIDE_PROPS) node.setStateStyle(part, state, prop, Number(value))
        node.resetStateStyle(part, state, 'padding')
    } else {
        node.setStateStyle(part, state, 'padding', Number(value))
    }
    for (const prop of [...PADDING_SIDE_PROPS, ...PADDING_AXIS_PROPS]) {
        if (!writeSides || !PADDING_SIDE_PROPS.includes(prop)) node.resetStateStyle(part, state, prop)
    }
}

/**
 * One side: write/update only that override, then — if the four effective
 * values have become equal again — normalize according to the editor mode.
 */
export function applySidePadding(node, part, state, sideProp, value, { overallScale = 1, writeSides = false } = {}) {
    node.setStateStyle(part, state, sideProp, Number(value))
    const resolved = node.style(part, state)
    if (isUniformPadding(resolved, { overallScale })) {
        applyOverallPadding(node, part, state, resolvePadding(resolved, { overallScale }).top, { writeSides })
    }
}

/** Reset the complete padding editor, including direct side values. */
export function resetOverallPadding(node, part, state) {
    for (const prop of ['padding', ...PADDING_SIDE_PROPS, ...PADDING_AXIS_PROPS]) {
        node.resetStateStyle(part, state, prop)
    }
}
